import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bot, KeyRound, Plug, Unplug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, Spinner } from '@/components/ui/misc';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

interface Connection {
  id: string;
  status: string;
  partner_name: string;
  partner_logo_url?: string;
  activated_at?: string;
  created_at: string;
  // Nulo = conta ainda gerida pelo parceiro, e por isso sem chave de API própria.
  standalone_at?: string;
}

interface AIConnection {
  id: string;
  client_name: string;
  user_name: string;
  user_email: string;
  project_ids: string[];
  scopes: string[];
  created_at: string;
  last_used_at?: string;
}

// ConnectedApps — softwares parceiros ligados a esta conta pelo bZapper Connect. O cliente
// vê quem usa o WhatsApp dele e desconecta quando quiser (a key do parceiro para na hora;
// plano e números continuam no bZapper).
export function ConnectedApps() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [target, setTarget] = useState<Connection | null>(null);
  const [converting, setConverting] = useState<Connection | null>(null);
  const [busy, setBusy] = useState(false);
  const [aiTarget, setAiTarget] = useState<AIConnection | null>(null);

  // Assistentes de IA (conector MCP): o admin vê os da conta; o atendente, os dele.
  const ai = useQuery({
    queryKey: ['ai-connections'],
    queryFn: async (): Promise<AIConnection[]> => {
      const r = await apiFetch('GET', '/ai-connections');
      return r.ok ? ((r.data as { data?: AIConnection[] })?.data ?? []) : [];
    },
  });

  const revokeAI = async () => {
    if (!aiTarget) return;
    setBusy(true);
    try {
      const r = await apiFetch('DELETE', `/ai-connections/${aiTarget.id}`);
      if (r.ok) {
        toast.success(t('connectedApps.aiRevoked'));
        setAiTarget(null);
        qc.invalidateQueries({ queryKey: ['ai-connections'] });
      } else toast.error(t('connectedApps.aiRevokeFailed'));
    } finally { setBusy(false); }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['connected-apps'],
    queryFn: async (): Promise<Connection[]> => {
      const r = await apiFetch('GET', '/me/connections');
      return r.ok ? ((r.data as { data?: Connection[] })?.data ?? []) : [];
    },
  });

  const revoke = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const r = await apiFetch('DELETE', `/me/connections/${target.id}`);
      if (r.ok) {
        toast.success(t('connectedApps.revoked'));
        setTarget(null);
        qc.invalidateQueries({ queryKey: ['connected-apps'] });
      } else toast.error(t('connectedApps.revokeFailed'));
    } finally { setBusy(false); }
  };

  // Converter NÃO desconecta: a conta deixa de ser gerida pela integração e passa a
  // emitir chave própria, com o parceiro seguindo ligado e o desconto dele valendo.
  const convert = async () => {
    if (!converting) return;
    setBusy(true);
    try {
      const r = await apiFetch('POST', `/me/connections/${converting.id}/standalone`);
      if (r.ok) {
        toast.success(t('connectedApps.converted'));
        setConverting(null);
        qc.invalidateQueries({ queryKey: ['connected-apps'] });
      } else toast.error(t('connectedApps.convertFailed'));
    } finally { setBusy(false); }
  };

  const status = (s: string) =>
    s === 'active' ? <Badge variant="success">{t('connectedApps.statusActive')}</Badge>
      : s === 'suspended' ? <Badge variant="destructive">{t('connectedApps.statusSuspended')}</Badge>
        : <Badge variant="secondary">{t('connectedApps.statusPending')}</Badge>;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">{t('connectedApps.subtitle')}</p>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : (data ?? []).length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <Plug className="size-8 text-muted-foreground" />
              <p className="max-w-md text-sm text-muted-foreground">{t('connectedApps.empty')}</p>
            </div>
          ) : (
            <ul className="divide-y">
              {(data ?? []).map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm">
                  {c.partner_logo_url
                    ? <img src={c.partner_logo_url} alt="" className="size-10 rounded-lg border object-contain" />
                    : <span className="grid size-10 place-items-center rounded-lg bg-primary/10 text-primary"><Plug className="size-5" /></span>}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{c.partner_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('connectedApps.since', { date: new Date(c.activated_at ?? c.created_at).toLocaleDateString(locale) })}
                    </div>
                  </div>
                  {status(c.status)}
                  {c.standalone_at ? (
                    <Badge variant="outline"><KeyRound className="size-3" /> {t('connectedApps.standalone')}</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setConverting(c)}>
                      <KeyRound className="size-3.5" /> {t('connectedApps.convert')}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setTarget(c)}>
                    <Unplug className="size-3.5" /> {t('connectedApps.revoke')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-base font-semibold">{t('connectedApps.aiTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('connectedApps.aiSubtitle')}</p>
        </div>
        <Card>
          <CardContent className="p-0">
            {ai.isLoading ? (
              <div className="space-y-3 p-5"><Skeleton className="h-14 w-full" /></div>
            ) : (ai.data ?? []).length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
                <Bot className="size-8 text-muted-foreground" />
                <p className="max-w-md text-sm text-muted-foreground">{t('connectedApps.aiEmpty')}</p>
              </div>
            ) : (
              <ul className="divide-y">
                {(ai.data ?? []).map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 px-5 py-4 text-sm">
                    <span className="grid size-10 place-items-center rounded-lg bg-primary/10 text-primary"><Bot className="size-5" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{c.client_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('connectedApps.aiBy', { name: c.user_name || c.user_email, date: new Date(c.created_at).toLocaleDateString(locale) })}
                        {' · '}
                        {c.last_used_at
                          ? t('connectedApps.aiLastUsed', { date: new Date(c.last_used_at).toLocaleString(locale) })
                          : t('connectedApps.aiNeverUsed')}
                        {' · '}
                        {t('connectedApps.aiProjects', { count: c.project_ids.length })}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {c.scopes.map((s) => (
                          <Badge key={s} variant="outline">{t(`aiConnect.scope.${s.replace('bzapper:', '')}.title`)}</Badge>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setAiTarget(c)}>
                      <Unplug className="size-3.5" /> {t('connectedApps.aiRevoke')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      <Dialog open={!!aiTarget} onOpenChange={(o) => !o && setAiTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('connectedApps.aiRevokeTitle', { name: aiTarget?.client_name ?? '' })}</DialogTitle>
            <DialogDescription>{t('connectedApps.aiRevokeDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiTarget(null)}>{t('connectedApps.cancel')}</Button>
            <Button variant="destructive" onClick={revokeAI} disabled={busy}>
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('connectedApps.aiRevoke')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('connectedApps.revokeTitle', { name: target?.partner_name ?? '' })}</DialogTitle>
            <DialogDescription>{t('connectedApps.revokeDesc', { name: target?.partner_name ?? '' })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)}>{t('connectedApps.cancel')}</Button>
            <Button variant="destructive" onClick={revoke} disabled={busy}>
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('connectedApps.revoke')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!converting} onOpenChange={(o) => !o && setConverting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('connectedApps.convertTitle', { name: converting?.partner_name ?? '' })}</DialogTitle>
            <DialogDescription>{t('connectedApps.convertDesc', { name: converting?.partner_name ?? '' })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConverting(null)}>{t('connectedApps.cancel')}</Button>
            <Button onClick={convert} disabled={busy}>
              {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />} {t('connectedApps.convertCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
